<template>
  <header class="landing-header-shell fixed top-0 left-0 right-0 shadow-sm z-50 transition-all duration-300 ease-in-out">
    <div class="mx-auto px-6">
      <div class="flex items-center justify-between h-16">
        <!-- Logo部分 -->
        <div class="flex items-center">
          <AppLogo class="w-auto h-8" :variant="'light'" @logo-click="trackLogoClicked" />
        </div>

        <!-- 导航链接 -->
        <nav class="hidden md:flex space-x-1">
          <!-- 首页入口：统一跳 LandingPage（/landing），不受登录态限制 -->
          <button
            class="nav-button landing-nav-neutral landing-nav-hover relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-md"
            @click="navigateToLanding"
            :title="$t('header.home')"
            :aria-label="$t('header.home')"
          >
            {{ $t('header.home') }}
          </button>
          <!-- Get Started Free：复用 commented early access 选项的 landing-nav-waitlist 样式 -->
          <button
            class="nav-button landing-nav-waitlist border-2 border-transparent transform hover:scale-102 bg-clip-padding relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-md"
            @click="navigateToUpload"
            :title="$t('landing.startFree')"
            :aria-label="$t('landing.startFree')"
          >
            {{ $t('landing.startFree') }}
          </button>
          <button v-for="(item, index) in navItems" :key="index"
            :class="[
              'nav-button relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-md',
              item.section === 'waitlist' 
                ? 'landing-nav-waitlist border-2 border-transparent transform hover:scale-102 bg-clip-padding'
                : 'landing-nav-neutral landing-nav-hover'
            ]"
            @click.prevent="scrollToSection(item.section)"
            :title="$t(item.label)"
            :aria-label="$t(item.label)">
            {{ $t(item.label) }}
            <span v-if="item.section !== 'waitlist'" class="landing-nav-underline absolute bottom-0 left-0 w-full h-0.5 transform scale-x-0 transition-transform duration-300 origin-left"></span>
          </button>
          <button 
            class="nav-button landing-nav-neutral landing-nav-hover relative px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-md"
            @click="navigateToTools">
            {{ $t('header.landing.tools') }}
          </button>
        </nav>

        <!-- 右侧按钮组 -->
        <div class="flex items-center space-x-4">
          <!-- 登录按钮 -->
          <UserMenu />
        </div>
      </div>
    </div>

    <!-- 滚动指示器 -->
    <div class="absolute bottom-0 left-0 w-full h-[2px] landing-header-divider">
      <div 
        class="landing-scroll-indicator h-full transition-all duration-300"
        :style="{ width: `${scrollProgress}%` }"
      ></div>
    </div>
  </header>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import AppLogo from '../AppLogo.vue';
import UserMenu from '../UserMenu.vue';
import { useRouter } from 'vue-router';
import { useLandingAnalytics } from '@/core/analytics/hooks/useLandingAnalytics';
import { useUserStore } from '@/core/store/useUserStore';
import { getLandingSections } from '@/core/landing/landingVariants';
import { useLandingVersion } from '@/core/landing/landingVersionContext';

const router = useRouter();
const analytics = useLandingAnalytics();
const scrollProgress = ref(0);
const scrollDelayTimer = ref(null);

// 导航项统一从 Section 目录派生：仅取 headerLabelKey 非空者，按当前版本清单顺序排列。
// item.section 对应目录的 DOM id（滚动锚点目标），item.label 对应 headerLabelKey（i18n key）。
// 原本注释隐藏的 waitlist 导航项见 landingVariants.js 中的保留说明。
const landingVersion = useLandingVersion();
const navItems = computed(() =>
  getLandingSections(landingVersion.value)
    .filter((section) => section.headerLabelKey !== null)
    .map((section) => ({
      label: section.headerLabelKey,
      section: section.id,
    }))
);

const updateScrollProgress = () => {
  const winScroll = window.scrollY;
  const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
  scrollProgress.value = (winScroll / height) * 100;
};

onMounted(() => {
  window.addEventListener('scroll', updateScrollProgress);
});

onUnmounted(() => {
  window.removeEventListener('scroll', updateScrollProgress);
  if (scrollDelayTimer.value) {
    clearTimeout(scrollDelayTimer.value);
    scrollDelayTimer.value = null;
  }
});

const waitForLandingPageRender = () => new Promise(resolve => {
  scrollDelayTimer.value = setTimeout(() => {
    scrollDelayTimer.value = null;
    resolve();
  }, 100);
});

const normalizeDestinationId = (sectionId) => sectionId.replace(/-/g, '_');

const getLandingContext = () => {
  let isLoggedIn = false;
  try {
    isLoggedIn = Boolean(useUserStore().isLoggedIn);
  } catch {
    isLoggedIn = false;
  }

  return {
    lang: router.currentRoute.value.params.lang,
    is_logged_in: isLoggedIn
  };
};

const trackNavigationCompleted = (payload) => {
  analytics.trackLandingCtaNavigationCompleted({
    ...getLandingContext(),
    source_position: 'landing_header',
    element_name: 'section_nav',
    element_type: 'button',
    ...payload
  });
};

// Logo 点击：与相邻 nav 按钮同域，走 landing_nav_clicked（hook 默认 source_position='landing_header'）。
// AppLogo 内部跳 /:lang/home（Landing 路由），与首页按钮的 /landing 目标不同，
// 但两者渲染同一 LandingPageView，故 destination_id 统一归一为 'landing'。
const trackLogoClicked = () => {
  analytics.trackLandingNavClicked({
    ...getLandingContext(),
    element_name: 'logo',
    element_type: 'link',
    cta_type: 'landing',
    destination_type: 'route',
    destination_id: 'landing',
    content_id: 'landing',
    content_type: 'route'
  });
};

const scrollToSection = async (sectionId) => {
  const startedAt = Date.now();
  const destinationId = normalizeDestinationId(sectionId);
  analytics.trackLandingNavClicked({
    ...getLandingContext(),
    element_name: 'section_nav',
    content_id: destinationId,
    content_type: 'landing_section',
    destination_type: 'section',
    destination_id: destinationId
  });
  const currentRoute = router.currentRoute.value;
  const currentLang = currentRoute.params.lang;

  // 是否已在 Landing 页面：/home 与 /landing 都渲染 LandingPageView（含全部 section）。
  // 已登录用户经顶部「首页」入口进入的是 /landing（name LandingPage），此时应直接页内滚动，
  // 不应再跳转 /home（否则会被登录态守卫重定向到 upload，导致 section 跳转失败）。
  const onLanding =
    currentRoute.path.endsWith('/home') || currentRoute.path.endsWith('/landing');
  if (!onLanding) {
    // 不在 Landing 页（如 /tools）：跳转到 /landing 别名而非 /home。
    // /landing（name LandingPage）不受登录态拦截，已登录用户也不会被弹到 upload；
    // 其 canonical 回指 /home，对 SEO 无影响。
    try {
      await router.push({ name: 'LandingPage', params: { lang: currentLang } });
    } catch {
      trackNavigationCompleted({
        action_status: 'failed',
        reason_code: 'router_push_failed',
        destination_type: 'section',
        destination_id: destinationId,
        duration_ms: Date.now() - startedAt
      });
      return;
    }
    // 添加延时等待页面加载
    await waitForLandingPageRender();
  }

  const section = document.getElementById(sectionId);
  if (section) {
    const headerOffset = 64;
    const elementPosition = section.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
    trackNavigationCompleted({
      action_status: 'success',
      destination_type: 'section',
      destination_id: destinationId,
      content_id: destinationId,
      content_type: 'landing_section',
      duration_ms: Date.now() - startedAt
    });
    return;
  }

  trackNavigationCompleted({
    action_status: 'failed',
    reason_code: 'section_target_missing',
    destination_type: 'section',
    destination_id: destinationId,
    duration_ms: Date.now() - startedAt
  });
};

const navigateToTools = async () => {
  const startedAt = Date.now();
  const currentLang = router.currentRoute.value.params.lang;
  analytics.trackLandingNavClicked({
    ...getLandingContext(),
    element_name: 'tools_nav',
    element_type: 'button',
    cta_type: 'tools',
    destination_type: 'route',
    destination_id: 'tools',
    content_id: 'tools',
    content_type: 'route'
  });

  try {
    await router.push(`/${currentLang}/tools`);
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: 'landing_header',
      element_name: 'tools_nav',
      element_type: 'button',
      action_status: 'success',
      cta_type: 'tools',
      destination_type: 'route',
      destination_id: 'tools',
      content_id: 'tools',
      content_type: 'route',
      duration_ms: Date.now() - startedAt
    });
  } catch {
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: 'landing_header',
      element_name: 'tools_nav',
      element_type: 'button',
      action_status: 'failed',
      reason_code: 'router_push_failed',
      cta_type: 'tools',
      destination_type: 'route',
      destination_id: 'tools',
      duration_ms: Date.now() - startedAt
    });
  }
};

const navigateToLanding = async () => {
  const startedAt = Date.now();
  const currentLang = router.currentRoute.value.params.lang || 'en';
  analytics.trackLandingNavClicked({
    ...getLandingContext(),
    element_name: 'home_nav',
    element_type: 'button',
    cta_type: 'landing',
    destination_type: 'route',
    destination_id: 'landing',
    content_id: 'landing',
    content_type: 'route'
  });

  try {
    await router.push({ name: 'LandingPage', params: { lang: currentLang } });
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: 'landing_header',
      element_name: 'home_nav',
      element_type: 'button',
      action_status: 'success',
      cta_type: 'landing',
      destination_type: 'route',
      destination_id: 'landing',
      content_id: 'landing',
      content_type: 'route',
      duration_ms: Date.now() - startedAt
    });
  } catch {
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: 'landing_header',
      element_name: 'home_nav',
      element_type: 'button',
      action_status: 'failed',
      reason_code: 'router_push_failed',
      cta_type: 'landing',
      destination_type: 'route',
      destination_id: 'landing',
      content_id: 'landing',
      content_type: 'route',
      duration_ms: Date.now() - startedAt
    });
  }
};

const navigateToUpload = async () => {
  const startedAt = Date.now();
  const currentLang = router.currentRoute.value.params.lang || 'en';
  analytics.trackLandingNavClicked({
    ...getLandingContext(),
    element_name: 'start_free_nav',
    element_type: 'button',
    cta_type: 'upload',
    destination_type: 'route',
    destination_id: 'upload',
    content_id: 'upload',
    content_type: 'route'
  });

  try {
    await router.push({ name: 'upload', params: { lang: currentLang } });
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: 'landing_header',
      element_name: 'start_free_nav',
      element_type: 'button',
      action_status: 'success',
      cta_type: 'upload',
      destination_type: 'route',
      destination_id: 'upload',
      content_id: 'upload',
      content_type: 'route',
      duration_ms: Date.now() - startedAt
    });
  } catch {
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: 'landing_header',
      element_name: 'start_free_nav',
      element_type: 'button',
      action_status: 'failed',
      reason_code: 'router_push_failed',
      cta_type: 'upload',
      destination_type: 'route',
      destination_id: 'upload',
      content_id: 'upload',
      content_type: 'route',
      duration_ms: Date.now() - startedAt
    });
  }
};
</script>

<style scoped>
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.hover\:scale-102:hover {
  transform: scale(1.02);
}

.nav-button {
  position: relative;
  overflow: hidden;
}

.landing-header-shell {
  background-color: var(--app-page-bg);
  border-bottom: 1px solid color-mix(in srgb, var(--border-default) 70%, transparent);
}

.landing-nav-neutral {
  color: var(--text-secondary);
}

.landing-nav-hover:hover {
  background: var(--surface-card-hover);
}

.landing-header-divider {
  background: var(--border-default);
}

.landing-nav-waitlist {
  color: var(--theme-primary);
  background-image: linear-gradient(
    to right,
    color-mix(in srgb, var(--theme-gradient-start) 10%, transparent),
    color-mix(in srgb, var(--theme-gradient-end) 10%, transparent)
  );
}

.landing-nav-waitlist:hover {
  color: var(--text-inverse);
  background-image: linear-gradient(to right, var(--theme-gradient-start), var(--theme-gradient-end));
}

.landing-nav-underline {
  background-image: linear-gradient(to right, var(--theme-gradient-start), var(--theme-gradient-end));
}

.nav-button:hover .landing-nav-underline {
  transform: scaleX(1);
}

.landing-scroll-indicator {
  background-image: linear-gradient(to right, var(--theme-gradient-start), var(--theme-gradient-end));
}

.nav-button::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(to right, var(--theme-gradient-start), var(--theme-gradient-end));
  transform: scaleX(0);
  transition: transform 0.3s ease;
  transform-origin: right;
}

.nav-button:hover::after {
  transform: scaleX(1);
  transform-origin: left;
}

/* 滚动时的样式 */
:global(body.scrolled) .landing-header-shell {
  box-shadow: 0 10px 24px color-mix(in srgb, var(--shadow-color) 70%, transparent);
  background-color: var(--app-page-bg);
  backdrop-filter: blur(8px);
}

/* 平滑过渡效果 */
.landing-header-shell {
  transition: all 0.3s ease-in-out;
}
</style>
